/**
 * Default date format for the application: Month, Day, Year.
 * Use for anything displayed to users: notifications, pages, doc gen display, print.
 * Unless explicitly stated otherwise, all user-facing dates should use this format.
 * Inputs for forms (e.g. <input type="date">) stay as YYYY-MM-DD from the API.
 *
 * Date-only values (e.g. milestone due_date, completed_date, open_date) are often
 * sent as ISO strings like "2024-03-15T00:00:00.000Z". Parsing those with new Date()
 * and then toLocaleDateString() applies the user's timezone, which can show the
 * previous day (e.g. US Central: midnight UTC = March 14 evening). So we detect
 * date-only values and format using the calendar date (YYYY-MM-DD) without timezone shift.
 *
 * @param {string|Date|null|undefined} value - Date string (ISO or YYYY-MM-DD) or Date object
 * @returns {string} Formatted date (e.g. "1/26/2025") or "—" for invalid or missing
 */
/**
 * Value for <input type="date"> — must be YYYY-MM-DD. API often returns ISO datetime.
 * @param {string|Date|null|undefined} value
 * @returns {string} '' if empty/invalid
 */
export function toDateInputValue(value) {
  if (value == null || value === '') return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = typeof value === 'string' ? value.trim() : String(value);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    const mm = us[1].padStart(2, '0');
    const dd = us[2].padStart(2, '0');
    return `${us[3]}-${mm}-${dd}`;
  }
  return '';
}

/**
 * True when a stored date is a legacy placeholder (migrated Access/ETL), not a real calendar date.
 * Common patterns: year 1901, or 1950 (incl. 1950-01-01 "decline" semantics used elsewhere in the app).
 * Use for milestone due/window display so placeholders don't appear as real dates.
 */
export function isLegacySentinelMilestoneDate(value) {
  if (value == null || value === '') return false;
  const s = typeof value === 'string' ? value.trim() : String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const y = parseInt(m[1], 10);
  return y === 1901 || y === 1950;
}

/** Parse YYYY-MM-DD as local calendar date for stable sort/compare (avoids UTC midnight shifts). */
export function parseDateOnlyLocalMs(value) {
  if (value == null || value === '') return 0;
  const s = typeof value === 'string' ? value.trim() : String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return 0;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/**
 * Whole calendar days from local today to a date-only value (YYYY-MM-DD from API).
 * Avoids `new Date("YYYY-MM-DD")` UTC/local bugs that skew "days until due" vs formatted display.
 * @returns {number|null}
 */
export function diffCalendarDaysFromToday(value) {
  if (value == null || value === '') return null;
  const s = typeof value === 'string' ? value.trim() : String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.round((target - today) / 86400000);
  }
  const due = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((due - today) / 86400000);
}

export function formatDisplayDate(value) {
  if (value == null || value === '') return '—';
  const s = typeof value === 'string' ? value.trim() : (value instanceof Date ? value.toISOString() : String(value));
  const dateOnlyMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})(T|$)/);
  if (dateOnlyMatch) {
    const [, y, m, d] = dateOnlyMatch;
    const year = parseInt(y, 10);
    const month = parseInt(m, 10) - 1;
    const day = parseInt(d, 10);
    const localDate = new Date(year, month, day);
    if (Number.isNaN(localDate.getTime())) return '—';
    return localDate.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
}
